import { ArrowLeft, X } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

/**
 * v2.10: a dialog that is a centred card on a desktop and a full-screen sheet with a Back control on a
 * phone (≤ 768 px, P4.2). Escape and the backdrop close it; focus moves into it on open.
 */
export function Modal({
  title,
  onClose,
  children,
  footer,
  testId,
  wide = false,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  testId?: string;
  wide?: boolean;
}) {
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey, true);
    panel.current?.focus();
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose]);
  // A portal on <body>: a parent with a transform or backdrop-filter (the phone's sticky call bar) would
  // otherwise become the containing block of this `position: fixed` sheet and trap it inside itself.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/40 md:items-center md:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        data-testid={testId}
        className={cn(
          "flex h-dvh w-full flex-col bg-surface outline-none md:h-auto md:max-h-[90dvh] md:rounded-[var(--radius-card)] md:border md:shadow-xl",
          wide ? "md:max-w-2xl" : "md:max-w-lg",
          "pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] md:pt-0 md:pb-0",
        )}
      >
        <header className="flex items-center gap-2 border-b px-3 py-2">
          <button
            type="button"
            onClick={onClose}
            aria-label="Back"
            className="-ml-1 inline-flex min-h-11 min-w-11 items-center justify-center rounded md:hidden"
            data-testid={testId ? `${testId}-back` : "modal-back"}
          >
            <ArrowLeft className="size-5" aria-hidden />
          </button>
          <h2 className="min-w-0 flex-1 truncate text-sm font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="hidden rounded p-1 text-fg-muted hover:bg-surface-2 md:inline-flex"
          >
            <X className="size-4" aria-hidden />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">{children}</div>
        {footer && <footer className="border-t px-3 py-2">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
